/* ---------------------------------------------------------------------------
   Public renderer for CMS "blocks" — the content behind custom pages (/p/:slug)
   and the optional Home extras region. Each block is { type, ...fields }; images
   arrive already resolved to { src, key, alt } by the content-read Lambda.

   Blocks reuse the site's existing global classes (.section, .container,
   .eyebrow, .h-lg, .lead) so they look native; blocks.css only adds the few
   grids the site doesn't already have. Rendering nothing for an empty list keeps
   the Home page pixel-identical until an admin adds a block.
--------------------------------------------------------------------------- */
import { Link } from "react-router-dom";
import "./blocks.css";

const isInternal = (href) => typeof href === "string" && href.startsWith("/");

function Heading({ eyebrow, title, lead }) {
  return (
    <div className="container">
      <div className="section-head section-head--center">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        {title && <h2 className="h-lg">{title}</h2>}
        {lead && <p className="lead mx-auto">{lead}</p>}
      </div>
    </div>
  );
}

function RichText({ paragraphs = [] }) {
  return (
    <div className="container">
      <div className="block-prose">
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </div>
  );
}

function ImageBlock({ image, caption }) {
  if (!image?.src) return null;
  return (
    <div className="container">
      <figure className="block-figure">
        <img src={image.src} alt={image.alt || caption || ""} loading="lazy" />
        {caption && <figcaption>{caption}</figcaption>}
      </figure>
    </div>
  );
}

function Gallery({ images = [] }) {
  const shown = images.filter((i) => i?.src);
  if (!shown.length) return null;
  return (
    <div className="container">
      <div className="block-gallery">
        {shown.map((img, i) => (
          <img key={i} src={img.src} alt={img.alt || ""} loading="lazy" />
        ))}
      </div>
    </div>
  );
}

function CardGrid({ cards = [] }) {
  if (!cards.length) return null;
  return (
    <div className="container">
      <div className="block-cards">
        {cards.map((c, i) => (
          <article className="block-card" key={i}>
            {c.image?.src && <img className="block-card-img" src={c.image.src} alt={c.image.alt || c.title || ""} loading="lazy" />}
            {c.title && <h3 className="block-card-title">{c.title}</h3>}
            {c.text && <p className="block-card-text">{c.text}</p>}
          </article>
        ))}
      </div>
    </div>
  );
}

function Stats({ items = [] }) {
  if (!items.length) return null;
  return (
    <div className="container">
      <div className="block-stats">
        {items.map((s, i) => (
          <div className="block-stat" key={i}>
            <strong>{s.value}</strong>
            <span>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Cta({ label, href }) {
  if (!label || !href) return null;
  return (
    <div className="container">
      <div className="block-cta">
        {isInternal(href) ? (
          <Link className="btn btn--primary" to={href}>{label}</Link>
        ) : (
          <a className="btn btn--primary" href={href} target="_blank" rel="noreferrer">{label}</a>
        )}
      </div>
    </div>
  );
}

const RENDERERS = {
  heading: Heading,
  richtext: RichText,
  image: ImageBlock,
  gallery: Gallery,
  cardgrid: CardGrid,
  stats: Stats,
  cta: Cta,
};

/** Render an array of blocks. Each block becomes its own <section>. */
export default function Blocks({ blocks }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  return (
    <>
      {blocks.map((block, i) => {
        const Renderer = RENDERERS[block?.type];
        if (!Renderer) return null;
        return (
          <section className="section block-section" key={i}>
            <Renderer {...block} />
          </section>
        );
      })}
    </>
  );
}
